import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { Input } from "@ember/component";
import { fn } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { isEmpty } from "@ember/utils";
import DsaModeration from "discourse/components/dsa-moderation";
import ReviewableQueuedPost from "discourse/components/reviewable/queued-post";
import { popupAjaxError } from "discourse/lib/ajax-error";
import ComboBox from "discourse/select-kit/components/combo-box";
import DModal from "discourse/ui-kit/d-modal";
import DTextarea from "discourse/ui-kit/d-textarea";
import { i18n } from "discourse-i18n";

const OTHER_REASON = "other_reason";

export default class ReviseAndRejectPostReviewable extends Component {
  @service siteSettings;

  @tracked reason = OTHER_REASON;
  @tracked customReason;
  @tracked feedback;
  @tracked submitting = false;

  get configuredReasons() {
    const reasons = this.siteSettings.reviewable_revision_reasons
      .split("|")
      .filter(Boolean)
      .map((reason) => ({ id: reason, name: reason }))
      .concat([
        {
          id: OTHER_REASON,
          name: i18n("review.revise_and_reject_post.other_reason"),
        },
      ]);
    return reasons;
  }

  get showCustomReason() {
    return this.reason === OTHER_REASON;
  }

  get sendPMDisabled() {
    return (
      isEmpty(this.reason) ||
      (this.reason === OTHER_REASON && isEmpty(this.customReason)) ||
      this.submitting
    );
  }

  @action
  async rejectAndSendPM(additionalData = {}) {
    this.submitting = true;

    try {
      await this.args.model.performConfirmed(this.args.model.action, {
        revise_reason: this.reason,
        revise_custom_reason: this.customReason,
        revise_feedback: this.feedback,
        ...additionalData,
      });
      this.args.closeModal();
    } catch (error) {
      popupAjaxError(error);
    } finally {
      this.submitting = false;
    }
  }

  <template>
    <DsaModeration
      @onSubmit={{this.rejectAndSendPM}}
      @options={{@model.classificationOptions}}
      as |flow|
    >
      <DModal
        class="revise-and-reject-reviewable"
        @closeModal={{@closeModal}}
        @title={{i18n "review.revise_and_reject_post.title"}}
      >
        <:belowHeader>
          <flow.Steps @disabled={{this.submitting}} />
        </:belowHeader>
        <:body>
          <flow.Body>
            <div class="revise-and-reject-reviewable__queued-post">
              <ReviewableQueuedPost @reviewable={{@model.reviewable}} />
            </div>

            <div class="control-group">
              <label class="control-label" for="reason">{{i18n
                  "review.revise_and_reject_post.reason"
                }}</label>
              <ComboBox
                class="revise-and-reject-reviewable__reason"
                @content={{this.configuredReasons}}
                @name="reason"
                @onChange={{fn (mut this.reason)}}
                @value={{this.reason}}
              />
            </div>

            {{#if this.showCustomReason}}
              <div class="control-group">
                <label class="control-label" for="custom_reason">{{i18n
                    "review.revise_and_reject_post.custom_reason"
                  }}</label>
                <Input
                  class="revise-and-reject-reviewable__custom-reason"
                  name="custom_reason"
                  @type="text"
                  @value={{this.customReason}}
                />
              </div>
            {{/if}}

            <div class="control-group">
              <label class="control-label" for="feedback">{{i18n
                  "review.revise_and_reject_post.feedback"
                }}
                <span class="revise-and-reject-reviewable__optional">({{i18n
                    "review.revise_and_reject_post.optional"
                  }})</span>
              </label>
              <DTextarea
                class="revise-and-reject-reviewable__feedback"
                @name="feedback"
                @onChange={{fn (mut this.feedback)}}
                @value={{this.feedback}}
              />
            </div>
          </flow.Body>
        </:body>
        <:footer>
          <flow.Footer
            @cancel={{@closeModal}}
            @disabled={{this.sendPMDisabled}}
            @isLoading={{this.submitting}}
            @submitClass="btn-primary"
            @submitLabel={{i18n "review.revise_and_reject_post.send_pm"}}
          />
        </:footer>
      </DModal>
    </DsaModeration>
  </template>
}
